import { ReactFacebookLoginInfo } from 'react-facebook-login';
import logLogInFailed from '@alltrails/amplitude/events/logLogInFailed';
import logSignUpFailed from '@alltrails/amplitude/events/logSignUpFailed';
import { ApiError } from '@alltrails/core/utils/api';
import logError from '@alltrails/core/utils/logError';
import { post } from '@alltrails/core/utils/post';
import LoginResponse from '../types/LoginResponse';
import { authSuccessCallback } from '../utils/AuthHandlers';

declare module 'react-facebook-login' {
  // `@types/react-facebook-login` package is missing the `expiresIn` property which the backend requires
  // we could also use AuthResponse from https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/facebook-js-sdk/index.d.ts#L478C15-L478C27
  interface ReactFacebookLoginInfo {
    expiresIn: number;
  }
}

export default function useFacebookHandler() {
  async function facebookHandler({
    clientId,
    loginInfo,
    handlerType,
    referralLink,
    grantType
  }: {
    clientId?: string;
    loginInfo: ReactFacebookLoginInfo;
    handlerType: 'login' | 'signup';
    referralLink?: string;
    grantType?: string | null;
  }) {
    let response;

    try {
      response = await post<LoginResponse>('/identity/auth/facebook', {
        client_id: clientId || process.env.NEXT_PUBLIC_IDENTITY_CLIENT_ID,
        facebook_id: loginInfo.userID,
        facebook_token: loginInfo.accessToken,
        facebook_expires: loginInfo.expiresIn,
        ...(referralLink && { referral_code: referralLink }),
        ...(grantType && { grant_type: grantType })
      });

      authSuccessCallback(response, 'facebook', response.is_collab_list_invite);
    } catch (e: any) {
      const failureType = e instanceof ApiError && e.data?.errors?.length > 0 ? e.data.errors[0].code : e.status;

      if (handlerType === 'signup') {
        logSignUpFailed({ authentication_type: 'facebook', failure_type: failureType });
      } else {
        logLogInFailed({ authentication_type: 'facebook', failure_type: failureType });
      }

      logError(new Error(`There was an error logging in to or signing up with Facebook: ${e} as ${e.message}`));
      throw e;
    }

    return response;
  }

  return facebookHandler;
}
